import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Checkbox } from '../ui/checkbox';
import { Textarea } from '../ui/textarea';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../ui/dropdown-menu';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { format, isValid } from 'date-fns';
import { fr } from 'date-fns/locale';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { 
  Search, Download, CheckCircle, Clock, Euro, FileText, 
  ChevronDown, ChevronRight, Loader2, XCircle, Ban, 
  FileSpreadsheet, Users, Calendar, Filter, RefreshCcw,
  Check, X, AlertTriangle, Pencil, ArrowRightLeft,
  Bookmark, BookOpen, CheckCheck, PlusCircle, User, Target, 
  Tent, ClipboardList, Trophy, Home, Handshake, FileCheck, Ticket,
  Menu, Lock, Receipt
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Safe date formatter - returns fallback if date is invalid
const safeFormatDate = (dateString, formatStr, options = {}) => {
  if (!dateString) return options.fallback || '';
  try {
    const date = new Date(dateString);
    if (!isValid(date)) return options.fallback || 'date invalide';
    return format(date, formatStr, { locale: fr, ...options });
  } catch {
    return options.fallback || 'date invalide';
  }
};

// Category icons as React components
const CategoryIcon = ({ type, className = "w-4 h-4" }) => {
  const icons = {
    cours_hebdo_proprio: <Bookmark className={className} />,
    cours_collectif_ponctuel: <BookOpen className={className} />,
    cours_inclus_forfait: <CheckCheck className={className} />,
    cours_hors_forfait: <PlusCircle className={className} />,
    cours_particulier: <User className={className} />,
    stage_equitation_travail: <Target className={className} />,
    stage_autre: <Tent className={className} />,
    stage_demi_journee: <Tent className={className} />,
    stage_journee: <Tent className={className} />,
    forfait: <ClipboardList className={className} />,
    forfait_competition: <Trophy className={className} />,
    pension: <Home className={className} />,
    demi_pension: <Handshake className={className} />,
    licence: <FileCheck className={className} />,
    adhesion: <Ticket className={className} />
  };
  return icons[type] || <FileText className={className} />;
};

// Category labels and colors
const categoryConfig = {
  cours_hebdo_proprio: { label: 'Cours collectif (tarif préférentiel)', color: 'bg-blue-100 text-blue-800' },
  cours_collectif_ponctuel: { label: 'Cours collectif ponctuel', color: 'bg-sky-100 text-sky-800' },
  cours_inclus_forfait: { label: 'Cours inclus forfait', color: 'bg-green-100 text-green-800' },
  cours_hors_forfait: { label: 'Cours hors forfait', color: 'bg-orange-100 text-orange-800' },
  cours_particulier: { label: 'Cours particulier', color: 'bg-indigo-100 text-indigo-800' },
  stage_equitation_travail: { label: 'Stage équitation de travail', color: 'bg-purple-100 text-purple-800' },
  stage_autre: { label: 'Stage', color: 'bg-violet-100 text-violet-800' },
  forfait: { label: 'Forfait trimestriel', color: 'bg-green-100 text-green-800' },
  forfait_competition: { label: 'Forfait compétition', color: 'bg-orange-100 text-orange-800' },
  pension: { label: 'Pension', color: 'bg-amber-100 text-amber-800' },
  demi_pension: { label: 'Demi-pension', color: 'bg-cyan-100 text-cyan-800' },
  licence: { label: 'Licence FFE', color: 'bg-red-100 text-red-800' },
  adhesion: { label: 'Adhésion club', color: 'bg-pink-100 text-pink-800' }
};

const roleLabels = {
  cavalier_proprio: 'Propriétaire',
  cavalier_pp: 'Prop. Partagé',
  cavalier_dp: 'Demi-Pension',
  cavalier_forfait: 'Forfait',
  cavalier_externe: 'Externe'
};

export const DecompteTab = ({ onInvoiceCreated, isActive = true, facturationEnabled = false }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [trimesters, setTrimesters] = useState([]);
  const [selectedTrimester, setSelectedTrimester] = useState(null);
  const [data, setData] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all'); // all, cavalier_forfait, cavalier_dp, etc.
  const [expandedCavalier, setExpandedCavalier] = useState(null);
  const [cavalierDetail, setCavalierDetail] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [showExcludeDialog, setShowExcludeDialog] = useState(false);
  const [excludeItem, setExcludeItem] = useState(null);
  const [excludeReason, setExcludeReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [editAmountItem, setEditAmountItem] = useState(null);
  const [editAmountValue, setEditAmountValue] = useState('');
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [convertItem, setConvertItem] = useState(null);
  const [showChangeTypeDialog, setShowChangeTypeDialog] = useState(false);
  const [changeTypeItem, setChangeTypeItem] = useState(null);
  const [selectedNewType, setSelectedNewType] = useState('');
  
  // Per-cavalier display filters: { cavalier_id: { showPending: true, showInvoiced: false } }
  const [cavalierFilters, setCavalierFilters] = useState({});
  
  // State for creating new item
  const [showCreateItemDialog, setShowCreateItemDialog] = useState(false);
  const [newItemType, setNewItemType] = useState('');
  const [newItemLabel, setNewItemLabel] = useState('');
  const [newItemAmount, setNewItemAmount] = useState('');
  const [newItemDate, setNewItemDate] = useState('');
  
  // State for creating invoice from invoiced items
  const [showCreateInvoiceDialog, setShowCreateInvoiceDialog] = useState(false);
  const [invoiceGroupItems, setInvoiceGroupItems] = useState([]);
  const [invoiceGroupDate, setInvoiceGroupDate] = useState('');
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  
  // Get filter for a specific cavalier (default: show pending only)
  const getCavalierFilter = (cavalierId) => {
    return cavalierFilters[cavalierId] || { showPending: true, showInvoiced: false };
  };
  
  // Toggle filter for a cavalier
  const toggleCavalierFilter = (cavalierId, filterType) => {
    setCavalierFilters(prev => {
      const current = prev[cavalierId] || { showPending: true, showInvoiced: false };
      return {
        ...prev,
        [cavalierId]: {
          ...current,
          [filterType]: !current[filterType]
        }
      };
    });
  };

  const isAdmin = () => user?.role_club === 'instructeur_admin';
  
  // Item type options for creating new items
  const newItemTypeOptions = [
    { value: 'cours_particulier', label: 'Cours particulier', defaultAmount: 45 },
    { value: 'cours_collectif_ponctuel', label: 'Cours collectif', defaultAmount: 30 },
    { value: 'cours_hors_forfait', label: 'Cours hors forfait', defaultAmount: 25 },
    { value: 'stage_autre', label: 'Stage', defaultAmount: 45 },
    { value: 'licence', label: 'Licence FFE', defaultAmount: 36 },
    { value: 'adhesion', label: 'Adhésion club', defaultAmount: 50 },
    { value: 'autre', label: 'Autre', defaultAmount: 0 },
  ];
  
  // Course type options with prices
  const courseTypeOptions = [
    { value: 'cours_hors_forfait', label: 'Cours hors forfait', defaultAmount: 25 },
    { value: 'cours_particulier', label: 'Cours particulier', defaultAmount: 45 },
    { value: 'cours_collectif_ponctuel', label: 'Cours collectif', defaultAmount: 30 },
    { value: 'stage_demi_journee', label: 'Stage 1/2 journée', defaultAmount: 40 },
    { value: 'stage_journee', label: 'Stage 1 journée', defaultAmount: 70 },
  ];

  // Role options for filter
  const roleOptions = [
    { value: 'all', label: 'Tous les profils' },
    { value: 'cavalier_forfait', label: 'Forfait' },
    { value: 'cavalier_dp', label: 'Demi-pensionnaire (DP)' },
    { value: 'cavalier_proprio', label: 'Propriétaire' },
    { value: 'cavalier_pp', label: 'Propriétaire partagé (PP)' },
    { value: 'cavalier_externe', label: 'Externe' },
  ];

  // Fetch trimesters on mount
  useEffect(() => {
    fetchTrimesters();
  }, []);

  // Fetch data when trimester changes
  useEffect(() => {
    if (selectedTrimester) {
      // Clear previous data and detail
      setExpandedCavalier(null);
      setCavalierDetail(null);
      fetchDecompteData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTrimester]);

  const fetchTrimesters = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/decompte/trimestres`);
      setTrimesters(response.data.trimesters || []);
      // Select current trimester by default
      const current = (response.data.trimesters || []).find(t => t.is_current);
      if (current) {
        setSelectedTrimester(current);
      }
    } catch (error) {
      toast.error('Erreur lors du chargement des trimestres');
    }
  };

  const fetchDecompteData = async () => {
    if (!selectedTrimester) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/decompte/cavaliers`, {
        params: {
          year: selectedTrimester.year,
          trimester: selectedTrimester.trimester
        }
      });
      setData(response.data);
      setLastRefresh(Date.now());
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Refresh data when tab becomes active (sync from Facturation)
  useEffect(() => {
    if (isActive && selectedTrimester && lastRefresh) {
      // Refresh data when returning to this tab
      fetchDecompteData();
      // Also refresh expanded cavalier detail if any
      if (expandedCavalier) {
        fetchCavalierDetail(expandedCavalier);
      }
    }
  }, [isActive]);

  const fetchCavalierDetail = async (cavalierId) => {
    if (!selectedTrimester) return;
    setLoadingDetail(true);
    try {
      const response = await axios.get(`${API_URL}/api/decompte/cavalier/${cavalierId}`, {
        params: {
          year: selectedTrimester.year,
          trimester: selectedTrimester.trimester
        }
      });
      setCavalierDetail(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement du détail');
    } finally {
      setLoadingDetail(false);
    }
  };

  const handleExpandCavalier = (cavalierId) => {
    if (expandedCavalier === cavalierId) {
      setExpandedCavalier(null);
      setCavalierDetail(null);
    } else {
      setExpandedCavalier(cavalierId);
      fetchCavalierDetail(cavalierId);
    }
  };

  // Helper to update item status locally without refetching
  const updateItemsStatusLocally = (itemIds, newStatus) => {
    const idsArray = Array.isArray(itemIds) ? itemIds : [itemIds];
    let pendingDelta = 0;
    let invoicedDelta = 0;
    let excludedDelta = 0;
    
    // Update cavalierDetail items locally and calculate deltas
    if (cavalierDetail) {
      setCavalierDetail(prev => {
        if (!prev) return prev;
        const newItems = { ...prev.items };
        
        // Update each category
        Object.keys(newItems).forEach(category => {
          newItems[category] = newItems[category].map(item => {
            if (idsArray.includes(item.id)) {
              const oldStatus = item.status;
              const amount = item.amount || 0;
              
              // Calculate delta for totals based on status change
              if (oldStatus === 'pending') {
                pendingDelta -= amount;
                if (newStatus === 'invoiced') invoicedDelta += amount;
                if (newStatus === 'excluded') excludedDelta += amount;
              } else if (oldStatus === 'invoiced') {
                invoicedDelta -= amount;
                if (newStatus === 'pending') pendingDelta += amount;
                if (newStatus === 'excluded') excludedDelta += amount;
              } else if (oldStatus === 'excluded') {
                excludedDelta -= amount;
                if (newStatus === 'pending') pendingDelta += amount;
                if (newStatus === 'invoiced') invoicedDelta += amount;
              }
              
              return { ...item, status: newStatus };
            }
            return item;
          });
        });
        
        return {
          ...prev,
          items: newItems,
          totals: {
            ...prev.totals,
            pending: Math.max(0, (prev.totals?.pending || 0) + pendingDelta),
            invoiced: Math.max(0, (prev.totals?.invoiced || 0) + invoicedDelta)
          }
        };
      });
    }
    
    // Also update the main data totals for the cavalier summary
    if (data && expandedCavalier) {
      setData(prev => {
        if (!prev) return prev;
        
        const newCavaliers = prev.cavaliers.map(cav => {
          if (cav.cavalier.id === expandedCavalier) {
            // Update the summary totals
            const newSummary = { ...cav.summary };
            newSummary.total_pending = Math.max(0, (newSummary.total_pending || 0) + pendingDelta);
            newSummary.total_invoiced = Math.max(0, (newSummary.total_invoiced || 0) + invoicedDelta);
            
            return {
              ...cav,
              summary: newSummary
            };
          }
          return cav;
        });
        
        // Also update global totals
        const newTotals = { ...prev.totals };
        newTotals.total_pending = Math.max(0, (newTotals.total_pending || 0) + pendingDelta);
        newTotals.total_invoiced = Math.max(0, (newTotals.total_invoiced || 0) + invoicedDelta);
        
        return { 
          ...prev, 
          cavaliers: newCavaliers,
          totals: newTotals
        };
      });
    }
  };

  // Highlight an item briefly
  const highlightItem = (itemId, color = '#dcfce7') => {
    requestAnimationFrame(() => {
      const element = document.querySelector(`[data-item-id="${itemId}"]`);
      if (element) {
        element.style.backgroundColor = color;
        element.style.transition = 'background-color 0.3s ease';
        setTimeout(() => {
          element.style.backgroundColor = '';
        }, 1200);
      }
    });
  };

  const handleMarkInvoiced = async (itemIds) => {
    if (!isAdmin()) {
      toast.error('Action réservée aux administrateurs');
      return;
    }
    const lastItemId = Array.isArray(itemIds) ? itemIds[itemIds.length - 1] : itemIds;
    
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/decompte/mark-invoiced`, {
        item_ids: Array.isArray(itemIds) ? itemIds : [itemIds]
      });
      toast.success('Élément(s) marqué(s) comme facturé(s)');
      
      // Update local state without refetching (no scroll disruption)
      updateItemsStatusLocally(itemIds, 'invoiced');
      setSelectedItems([]);
      
      // Highlight the item
      highlightItem(lastItemId, '#dcfce7');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setActionLoading(false);
    }
  };

  const handleExclude = async () => {
    if (!excludeItem || !excludeReason.trim()) {
      toast.error('Veuillez indiquer une raison');
      return;
    }
    const itemId = excludeItem.id;
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/decompte/exclude`, {
        item_id: itemId,
        reason: excludeReason
      });
      toast.success('Élément exclu de la facturation');
      setShowExcludeDialog(false);
      setExcludeItem(null);
      setExcludeReason('');
      
      // Update local state without refetching
      updateItemsStatusLocally(itemId, 'excluded');
      highlightItem(itemId, '#f5f5f4');
    } catch (error) {
      toast.error('Erreur lors de l\'exclusion');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateAmount = async () => {
    if (!editAmountItem || !editAmountValue) return;
    const amount = parseFloat(editAmountValue);
    if (isNaN(amount) || amount < 0) {
      toast.error('Montant invalide');
      return;
    }
    const itemId = editAmountItem.id;
    const oldAmount = editAmountItem.amount || 0;
    const amountDiff = amount - oldAmount;
    const itemStatus = editAmountItem.status;
    
    setActionLoading(true);
    try {
      await axios.put(`${API_URL}/api/decompte/item/${itemId}/amount`, {
        amount: amount
      });
      toast.success('Montant mis à jour');
      setEditAmountItem(null);
      setEditAmountValue('');
      
      // Update local state without refetching
      if (cavalierDetail) {
        setCavalierDetail(prev => {
          if (!prev) return prev;
          const newItems = { ...prev.items };
          Object.keys(newItems).forEach(category => {
            newItems[category] = newItems[category].map(item => {
              if (item.id === itemId) {
                return { ...item, amount: amount, amount_to_define: false };
              }
              return item;
            });
          });
          
          // Update totals based on item status
          const newTotals = { ...prev.totals };
          if (itemStatus === 'pending') {
            newTotals.pending = Math.max(0, (newTotals.pending || 0) + amountDiff);
          } else if (itemStatus === 'invoiced') {
            newTotals.invoiced = Math.max(0, (newTotals.invoiced || 0) + amountDiff);
          }
          
          return { ...prev, items: newItems, totals: newTotals };
        });
      }
      
      // Also update the main data totals
      if (data && expandedCavalier) {
        setData(prev => {
          if (!prev) return prev;
          
          const newCavaliers = prev.cavaliers.map(cav => {
            if (cav.cavalier.id === expandedCavalier) {
              const newSummary = { ...cav.summary };
              if (itemStatus === 'pending') {
                newSummary.total_pending = Math.max(0, (newSummary.total_pending || 0) + amountDiff);
              } else if (itemStatus === 'invoiced') {
                newSummary.total_invoiced = Math.max(0, (newSummary.total_invoiced || 0) + amountDiff);
              }
              return { ...cav, summary: newSummary };
            }
            return cav;
          });
          
          // Update global totals
          const newTotals = { ...prev.totals };
          if (itemStatus === 'pending') {
            newTotals.total_pending = Math.max(0, (newTotals.total_pending || 0) + amountDiff);
          } else if (itemStatus === 'invoiced') {
            newTotals.total_invoiced = Math.max(0, (newTotals.total_invoiced || 0) + amountDiff);
          }
          
          return { ...prev, cavaliers: newCavaliers, totals: newTotals };
        });
      }
      
      highlightItem(itemId, '#dbeafe');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du montant');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetStatus = async (itemId) => {
    setActionLoading(true);
    try {
      await axios.put(`${API_URL}/api/decompte/item/${itemId}/status`, {
        item_id: itemId,
        status: 'pending'
      });
      toast.success('Statut réinitialisé');
      
      // Update local state without refetching (no scroll disruption)
      updateItemsStatusLocally(itemId, 'pending');
      
      // Highlight the item
      highlightItem(itemId, '#fef3c7');
    } catch (error) {
      toast.error('Erreur lors de la réinitialisation');
    } finally {
      setActionLoading(false);
    }
  };

  // Bulk reset status (defacturer)
  const handleBulkResetStatus = async (itemIds) => {
    const lastItemId = Array.isArray(itemIds) ? itemIds[itemIds.length - 1] : itemIds;
    
    setActionLoading(true);
    try {
      // Reset each item to pending
      await Promise.all(
        itemIds.map(id => 
          axios.put(`${API_URL}/api/decompte/item/${id}/status`, {
            item_id: id,
            status: 'pending'
          })
        )
      );
      toast.success(`${itemIds.length} élément(s) défacturé(s)`);
      setSelectedItems([]);
      
      // Update local state without refetching (no scroll disruption)
      updateItemsStatusLocally(itemIds, 'pending');
      
      // Highlight the last item
      highlightItem(lastItemId, '#fef3c7');
    } catch (error) {
      toast.error('Erreur lors de la défacturation');
    } finally {
      setActionLoading(false);
    }
  };

  // Bulk exclude items (without reason)
  const handleBulkExclude = async (itemIds) => {
    if (!isAdmin()) {
      toast.error('Action réservée aux administrateurs');
      return;
    }
    const lastItemId = Array.isArray(itemIds) ? itemIds[itemIds.length - 1] : itemIds;
    
    setActionLoading(true);
    try {
      await Promise.all(
        itemIds.map(id => 
          axios.post(`${API_URL}/api/decompte/exclude`, {
            item_id: id,
            reason: 'Exclusion groupée'
          })
        )
      );
      toast.success(`${itemIds.length} élément(s) exclu(s)`);
      setSelectedItems([]);
      
      // Update local state without refetching
      updateItemsStatusLocally(itemIds, 'excluded');
      highlightItem(lastItemId, '#f5f5f4');
    } catch (error) {
      toast.error('Erreur lors de l\'exclusion');
    } finally {
      setActionLoading(false);
    }
  };

  // State for bulk change type
  const [showBulkChangeTypeDialog, setShowBulkChangeTypeDialog] = useState(false);
  const [bulkChangeTypeItems, setBulkChangeTypeItems] = useState([]);
  const [bulkSelectedNewType, setBulkSelectedNewType] = useState('');
  const [bulkNewAmount, setBulkNewAmount] = useState('');

  // Bulk change type
  const handleBulkChangeType = async () => {
    if (!bulkSelectedNewType || !bulkNewAmount) {
      toast.error('Veuillez sélectionner un type et un montant');
      return;
    }
    const currentCavalier = expandedCavalier;
    const newAmount = parseFloat(bulkNewAmount);
    
    // Calculate total amount difference
    let pendingDiff = 0;
    let invoicedDiff = 0;
    bulkChangeTypeItems.forEach(item => {
      const oldAmount = item.amount || 0;
      const diff = newAmount - oldAmount;
      if (item.status === 'pending') {
        pendingDiff += diff;
      } else if (item.status === 'invoiced') {
        invoicedDiff += diff;
      }
    });
    
    setActionLoading(true);
    try {
      await Promise.all(
        bulkChangeTypeItems.map(item => 
          axios.post(`${API_URL}/api/decompte/change-type`, {
            item_id: item.id,
            new_type: bulkSelectedNewType,
            amount: newAmount
          })
        )
      );
      toast.success(`${bulkChangeTypeItems.length} élément(s) modifié(s)`);
      setShowBulkChangeTypeDialog(false);
      setBulkChangeTypeItems([]);
      setBulkSelectedNewType('');
      setBulkNewAmount('');
      setSelectedItems([]);
      
      // Update global totals if amounts changed
      if ((pendingDiff !== 0 || invoicedDiff !== 0) && data && expandedCavalier) {
        setData(prev => {
          if (!prev) return prev;
          
          const newCavaliers = prev.cavaliers.map(cav => {
            if (cav.cavalier.id === expandedCavalier) {
              const newSummary = { ...cav.summary };
              newSummary.total_pending = Math.max(0, (newSummary.total_pending || 0) + pendingDiff);
              newSummary.total_invoiced = Math.max(0, (newSummary.total_invoiced || 0) + invoicedDiff);
              return { ...cav, summary: newSummary };
            }
            return cav;
          });
          
          const newTotals = { ...prev.totals };
          newTotals.total_pending = Math.max(0, (newTotals.total_pending || 0) + pendingDiff);
          newTotals.total_invoiced = Math.max(0, (newTotals.total_invoiced || 0) + invoicedDiff);
          
          return { ...prev, cavaliers: newCavaliers, totals: newTotals };
        });
      }
      
      // Refetch the detail
      if (currentCavalier) {
        await fetchCavalierDetail(currentCavalier);
      }
    } catch (error) {
      toast.error('Erreur lors de la modification');
    } finally {
      setActionLoading(false);
    }
  };

  // Create new item for a cavalier
  const handleCreateItem = async () => {
    if (!newItemType || !newItemAmount || !newItemLabel) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    const amount = parseFloat(newItemAmount);
    if (isNaN(amount) || amount < 0) {
      toast.error('Montant invalide');
      return;
    }
    
    const currentCavalier = expandedCavalier;
    if (!currentCavalier || !selectedTrimester) {
      toast.error('Erreur: cavalier ou trimestre non sélectionné');
      return;
    }
    
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/decompte/item/create`, {
        cavalier_id: currentCavalier,
        period: `${selectedTrimester.year}-T${selectedTrimester.trimester}`,
        item_type: newItemType,
        label: newItemLabel,
        amount: amount,
        date: newItemDate || null
      });
      
      toast.success('Ligne de facturation ajoutée');
      setShowCreateItemDialog(false);
      setNewItemType('');
      setNewItemLabel('');
      setNewItemAmount('');
      setNewItemDate('');
      
      // Update totals locally
      if (data) {
        setData(prev => {
          if (!prev) return prev;
          
          const newCavaliers = prev.cavaliers.map(cav => {
            if (cav.cavalier.id === currentCavalier) {
              const newSummary = { ...cav.summary };
              newSummary.total_pending = (newSummary.total_pending || 0) + amount;
              return { ...cav, summary: newSummary };
            }
            return cav;
          });
          
          const newTotals = { ...prev.totals };
          newTotals.total_pending = (newTotals.total_pending || 0) + amount;
          
          return { ...prev, cavaliers: newCavaliers, totals: newTotals };
        });
      }
      
      // Refetch detail to show new item
      if (currentCavalier) {
        await fetchCavalierDetail(currentCavalier);
      }
    } catch (error) {
      console.error('Error creating item:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setActionLoading(false);
    }
  };

  // Open bulk change type dialog with selected items
  const openBulkChangeTypeDialog = () => {
    if (!cavalierDetail || selectedItems.length === 0) return;
    
    // Get selected items details
    const items = Object.values(cavalierDetail.items || {})
      .flat()
      .filter(item => selectedItems.includes(item.id));
    
    setBulkChangeTypeItems(items);
    setBulkSelectedNewType('');
    setBulkNewAmount('');
    setShowBulkChangeTypeDialog(true);
  };

  // Group invoiced items by invoiced_at date
  const getInvoicedGroups = () => {
    if (!cavalierDetail?.items) return [];
    
    const groups = {};
    Object.values(cavalierDetail.items).flat().forEach(item => {
      if (item.status !== 'invoiced') return;
      
      const invoicedAt = item.invoiced_at?.slice(0, 10) || 'unknown';
      if (!groups[invoicedAt]) {
        groups[invoicedAt] = {
          date: invoicedAt,
          items: [],
          total: 0,
          locked: false,
          invoice_id: null,
          invoice_numero: null
        };
      }
      
      groups[invoicedAt].items.push(item);
      groups[invoicedAt].total += item.amount || 0;
      
      if (item.locked) {
        groups[invoicedAt].locked = true;
        groups[invoicedAt].invoice_id = item.invoice_id;
        groups[invoicedAt].invoice_numero = item.invoice_numero;
      }
    });
    
    return Object.values(groups).sort((a, b) => b.date.localeCompare(a.date));
  };

  // Open dialog to create invoice from a group of invoiced items
  const openCreateInvoiceDialog = (group) => {
    setInvoiceGroupItems(group.items);
    setInvoiceGroupDate(group.date);
    setShowCreateInvoiceDialog(true);
  };

  // Open dialog to create invoice directly from selected pending items
  const openCreateInvoiceFromSelection = () => {
    if (!cavalierDetail || selectedItems.length === 0) return;
    
    // Get selected items that are pending (not already invoiced or locked)
    const items = Object.values(cavalierDetail.items || {})
      .flat()
      .filter(item => selectedItems.includes(item.id) && item.status === 'pending' && !item.locked);
    
    if (items.length === 0) {
      toast.error('Aucun élément "À facturer" sélectionné');
      return;
    }
    
    // Use today's date as the invoiced_at date
    const today = new Date().toISOString().slice(0, 10);
    setInvoiceGroupItems(items);
    setInvoiceGroupDate(today);
    setShowCreateInvoiceDialog(true);
  };

  // Create invoice from invoiced items (or directly from pending items)
  const handleCreateInvoiceFromDecompte = async () => {
    if (!invoiceGroupItems.length || !expandedCavalier) return;
    
    setCreatingInvoice(true);
    try {
      const response = await axios.post(`${API_URL}/api/decompte/create-invoice`, {
        cavalier_id: expandedCavalier,
        item_ids: invoiceGroupItems.map(i => i.id)
      });
      
      // Show success message with instruction to go to Facturation tab
      toast.success(
        <div>
          <div className="font-medium">Brouillon créé avec succès</div>
          <div className="text-sm mt-1">
            Finalisez-le dans l'onglet <strong>Facturation</strong>
          </div>
        </div>,
        { duration: 5000 }
      );
      
      setShowCreateInvoiceDialog(false);
      setInvoiceGroupItems([]);
      setInvoiceGroupDate('');
      setSelectedItems([]); // Clear selection after invoice creation
      
      // Refetch detail to show locked status
      await fetchCavalierDetail(expandedCavalier);
      
      // Notify parent to refresh invoices list
      if (onInvoiceCreated) {
        onInvoiceCreated();
      }
    } catch (error) {
      console.error('Error creating invoice:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création de la facture');
    } finally {
      setCreatingInvoice(false);
    }
  };

  // Check if an item is locked (linked to an invoice)
  const isItemLocked = (item) => {
    return item.locked === true;
  };

  // Filter cavaliers - show all cavaliers with any items (pending or invoiced)
  const filteredCavaliers = useMemo(() => {
    if (!data?.cavaliers) return [];
    
    return data.cavaliers.filter(item => {
      const cav = item.cavalier;
      const sum = item.summary;
      
      // Search filter
      const matchesSearch = !searchTerm || 
        `${cav.prenom} ${cav.nom}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
        cav.email?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Show cavaliers with any items (pending or invoiced)
      const hasItems = sum.total_pending > 0 || sum.total_invoiced > 0;
      
      // Role filter
      let matchesRole = true;
      if (roleFilter !== 'all') {
        matchesRole = cav.role_club === roleFilter;
      }
      
      return matchesSearch && hasItems && matchesRole;
    });
  }, [data, searchTerm, roleFilter]);

  // Convert a "cours inclus forfait" to "cours hors forfait" (make it billable)
  // Note: This changes the item type so we need a refetch to move it to the right category
  const handleConvertToHorsForfait = async (item, amount) => {
    const currentCavalier = expandedCavalier;
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/decompte/convert-to-hors-forfait`, {
        item_id: item.id,
        amount: parseFloat(amount)
      });
      toast.success('Cours converti en hors forfait');
      setShowConvertDialog(false);
      setConvertItem(null);
      
      // Refetch detail and global data
      if (currentCavalier) {
        await fetchCavalierDetail(currentCavalier);
        // Also refetch global data to update summary
        await fetchData();
      }
    } catch (error) {
      toast.error('Erreur lors de la conversion');
    } finally {
      setActionLoading(false);
    }
  };

  // Change course type
  // Note: This changes the item type so we need a refetch to move it to the right category
  const handleChangeType = async (item, newType, amount) => {
    const currentCavalier = expandedCavalier;
    const oldAmount = item.amount || 0;
    const newAmount = parseFloat(amount);
    const amountDiff = newAmount - oldAmount;
    const itemStatus = item.status;
    
    setActionLoading(true);
    try {
      await axios.post(`${API_URL}/api/decompte/change-type`, {
        item_id: item.id,
        new_type: newType,
        amount: newAmount
      });
      toast.success('Type de cours modifié');
      setShowChangeTypeDialog(false);
      setChangeTypeItem(null);
      setSelectedNewType('');
      
      // Update global totals if amount changed
      if (amountDiff !== 0 && data && expandedCavalier) {
        setData(prev => {
          if (!prev) return prev;
          
          const newCavaliers = prev.cavaliers.map(cav => {
            if (cav.cavalier.id === expandedCavalier) {
              const newSummary = { ...cav.summary };
              if (itemStatus === 'pending') {
                newSummary.total_pending = Math.max(0, (newSummary.total_pending || 0) + amountDiff);
              } else if (itemStatus === 'invoiced') {
                newSummary.total_invoiced = Math.max(0, (newSummary.total_invoiced || 0) + amountDiff);
              }
              return { ...cav, summary: newSummary };
            }
            return cav;
          });
          
          const newTotals = { ...prev.totals };
          if (itemStatus === 'pending') {
            newTotals.total_pending = Math.max(0, (newTotals.total_pending || 0) + amountDiff);
          } else if (itemStatus === 'invoiced') {
            newTotals.total_invoiced = Math.max(0, (newTotals.total_invoiced || 0) + amountDiff);
          }
          
          return { ...prev, cavaliers: newCavaliers, totals: newTotals };
        });
      }
      
      // Refetch only the detail (item moved to another category)
      if (currentCavalier) {
        await fetchCavalierDetail(currentCavalier);
      }
    } catch (error) {
      toast.error('Erreur lors de la modification');
    } finally {
      setActionLoading(false);
    }
  };

  // Export functions
  const handleExportExcel = async () => {
    try {
      if (!filteredCavaliers || filteredCavaliers.length === 0) {
        toast.error('Aucune donnée à exporter');
        return;
      }

      toast.info('Chargement des données pour l\'export...');

      // Fetch all items for all cavaliers
      const allItems = [];
      const errors = [];
      
      for (const cavalierData of filteredCavaliers) {
        try {
          const cavalierName = `${cavalierData.cavalier?.prenom || ''} ${cavalierData.cavalier?.nom || ''}`.trim();
          const cavalierEmail = cavalierData.cavalier?.email || '';
          
          const response = await axios.get(`${API_URL}/api/decompte/cavalier/${cavalierData.cavalier.id}`, {
            params: {
              year: selectedTrimester?.year,
              trimester: selectedTrimester?.trimester
            }
          });
          
          // items is an object with categories as keys, flatten it
          const itemsObj = response.data?.items || {};
          const flatItems = Object.values(itemsObj).flat();
          
          flatItems.forEach(item => {
            allItems.push({
              'Cavalier': cavalierName,
              'Email': cavalierEmail,
              'Type': item.category_label || item.item_type || '',
              'Description': item.details?.label || item.details?.description || '',
              'Date': item.details?.date ? safeFormatDate(item.details.date, 'dd/MM/yyyy') : '',
              'Montant': item.amount || 0,
              'Statut': item.status === 'invoiced' ? 'Facturé' : item.status === 'excluded' ? 'Exclu' : 'À facturer',
              'Facturé le': item.invoiced_at ? safeFormatDate(item.invoiced_at, 'dd/MM/yyyy') : '',
            });
          });
        } catch (err) {
          console.error(`Error fetching items for cavalier:`, cavalierData.cavalier?.id, err);
          errors.push(cavalierData.cavalier?.id);
        }
      }

      if (allItems.length === 0) {
        toast.error('Aucune donnée à exporter');
        return;
      }

      // Create workbook
      const ws = XLSX.utils.json_to_sheet(allItems);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Décompte');

      // Auto-size columns
      const colWidths = Object.keys(allItems[0] || {}).map(key => ({
        wch: Math.max(key.length, ...allItems.map(row => String(row[key] || '').length))
      }));
      ws['!cols'] = colWidths;

      // Generate filename with trimester
      const filename = `decompte_${selectedTrimester?.label?.replace(/\s/g, '_') || 'export'}.xlsx`;
      
      // Download
      XLSX.writeFile(wb, filename);
      toast.success(`Export Excel téléchargé (${allItems.length} lignes)`);
    } catch (error) {
      console.error('Erreur export Excel:', error);
      toast.error('Erreur lors de l\'export Excel: ' + (error.message || 'Erreur inconnue'));
    }
  };

  const handleExportPDF = async () => {
    try {
      if (!filteredCavaliers || filteredCavaliers.length === 0) {
        toast.error('Aucune donnée à exporter');
        return;
      }

      toast.info('Chargement des données pour l\'export...');

      // Create PDF
      const doc = new jsPDF('l', 'mm', 'a4'); // Landscape
      
      // Title
      doc.setFontSize(16);
      doc.text(`Décompte financier - ${selectedTrimester?.label || ''}`, 14, 15);
      
      // Subtitle with totals
      doc.setFontSize(10);
      const totalPending = filteredCavaliers.reduce((sum, c) => sum + (c.summary?.total_pending || 0), 0);
      const totalInvoiced = filteredCavaliers.reduce((sum, c) => sum + (c.summary?.total_invoiced || 0), 0);
      doc.text(`À facturer: ${formatAmount(totalPending)} | Facturé: ${formatAmount(totalInvoiced)}`, 14, 22);

      // Fetch all items for all cavaliers
      const tableData = [];
      for (const cavalierData of filteredCavaliers) {
        try {
          const cavalierName = `${cavalierData.cavalier?.prenom || ''} ${cavalierData.cavalier?.nom || ''}`.trim();
          
          const response = await axios.get(`${API_URL}/api/decompte/cavalier/${cavalierData.cavalier.id}`, {
            params: {
              year: selectedTrimester?.year,
              trimester: selectedTrimester?.trimester
            }
          });
          
          // items is an object with categories as keys, flatten it
          const itemsObj = response.data?.items || {};
          const flatItems = Object.values(itemsObj).flat();
          
          flatItems.forEach(item => {
            tableData.push([
              cavalierName,
              item.category_label || item.item_type || '',
              item.details?.label || item.details?.description || '',
              item.details?.date ? safeFormatDate(item.details.date, 'dd/MM/yyyy') : '',
              formatAmount(item.amount || 0),
              item.status === 'invoiced' ? 'Facturé' : item.status === 'excluded' ? 'Exclu' : 'À facturer',
            ]);
          });
        } catch (err) {
          console.error(`Error fetching items for cavalier:`, cavalierData.cavalier?.id, err);
        }
      }

      if (tableData.length === 0) {
        toast.error('Aucune donnée à exporter');
        return;
      }

      // Add table
      autoTable(doc, {
        startY: 28,
        head: [['Cavalier', 'Type', 'Description', 'Date', 'Montant', 'Statut']],
        body: tableData,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [46, 74, 61] }, // #2E4A3D
        columnStyles: {
          0: { cellWidth: 40 },
          1: { cellWidth: 35 },
          2: { cellWidth: 80 },
          3: { cellWidth: 25 },
          4: { cellWidth: 25, halign: 'right' },
          5: { cellWidth: 25 },
        },
      });

      // Generate filename
      const filename = `decompte_${selectedTrimester?.label?.replace(/\s/g, '_') || 'export'}.pdf`;
      
      // Download
      doc.save(filename);
      toast.success(`Export PDF téléchargé (${tableData.length} lignes)`);
    } catch (error) {
      console.error('Erreur export PDF:', error);
      toast.error('Erreur lors de l\'export PDF: ' + (error.message || 'Erreur inconnue'));
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  // Check if an item is in the future (cannot be invoiced)
  const isItemInFuture = (item) => {
    const itemDate = item.details?.date;
    if (!itemDate) return false;
    const today = new Date();
    today.setHours(23, 59, 59, 999); // End of today
    return new Date(itemDate) > today;
  };

  // Filter items that can be invoiced (not in future)
  const getInvoiceableItems = (items) => {
    return items.filter(item => item.status === 'pending' && !isItemInFuture(item));
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#2E4A3D]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with trimester selector */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-stone-800">Décompte financier</h2>
          <p className="text-sm text-stone-500">
            Suivi des activités facturables par cavalier
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <Select
            value={selectedTrimester ? `${selectedTrimester.year}-${selectedTrimester.trimester}` : ''}
            onValueChange={(v) => {
              const [year, trim] = v.split('-');
              const t = trimesters.find(t => t.year === parseInt(year) && t.trimester === parseInt(trim));
              setSelectedTrimester(t);
            }}
          >
            <SelectTrigger className="w-[150px]">
              <Calendar className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Trimestre" />
            </SelectTrigger>
            <SelectContent>
              {trimesters.map(t => (
                <SelectItem key={`${t.year}-${t.trimester}`} value={`${t.year}-${t.trimester}`}>
                  {t.label} {t.is_current && '(actuel)'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button variant="outline" size="sm" onClick={handleExportExcel}>
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Excel
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportPDF}>
            <FileText className="w-4 h-4 mr-2" />
            PDF
          </Button>
        </div>
      </div>

      {/* Summary cards */}
      {data?.totals && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-sm text-stone-500">À facturer</div>
              <div className="text-2xl font-bold text-amber-600">{formatAmount(data.totals.total_pending)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-sm text-stone-500">Facturé</div>
              <div className="text-2xl font-bold text-green-600">{formatAmount(data.totals.total_invoiced)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-sm text-stone-500">Cavaliers</div>
              <div className="text-2xl font-bold text-stone-800">{data.cavaliers?.length || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-sm text-stone-500">Période</div>
              <div className="text-lg font-medium text-stone-800">
                {selectedTrimester?.label}
              </div>
              <div className="text-xs text-stone-400">
                {selectedTrimester?.start_date && format(new Date(selectedTrimester.start_date), 'd MMM', { locale: fr })} - {selectedTrimester?.end_date && format(new Date(selectedTrimester.end_date), 'd MMM yyyy', { locale: fr })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input
            placeholder="Rechercher un cavalier..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-[200px]">
            <Users className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {roleOptions.map(option => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Main table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead className="w-10"></TableHead>
                <TableHead>Cavalier</TableHead>
                <TableHead className="text-center">Rôle</TableHead>
                <TableHead className="text-center">Afficher</TableHead>
                <TableHead className="text-right">Cours</TableHead>
                <TableHead className="text-right">Forfaits</TableHead>
                <TableHead className="text-right">Pension/DP</TableHead>
                <TableHead className="text-right text-amber-700">À facturer</TableHead>
                <TableHead className="text-right text-green-700">Facturé</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCavaliers.map((item) => {
                const cav = item.cavalier;
                const sum = item.summary;
                const isExpanded = expandedCavalier === cav.id;
                const filter = getCavalierFilter(cav.id);
                
                // If this cavalier is expanded and we have detail data, use it for accurate counts
                const useDetailData = isExpanded && cavalierDetail && cavalierDetail.cavalier?.id === cav.id;
                
                // Calculate display totals based on filter
                // Use cavalierDetail.totals if available for more accurate counts after actions
                const pendingTotal = useDetailData ? (cavalierDetail.totals?.pending || 0) : sum.total_pending;
                const invoicedTotal = useDetailData ? (cavalierDetail.totals?.invoiced || 0) : sum.total_invoiced;
                const displayPending = filter.showPending ? pendingTotal : 0;
                const displayInvoiced = filter.showInvoiced ? invoicedTotal : 0;
                
                // Calculate course counts based on filter and detail data if available
                let coursPendingCount, coursInvoicedCount;
                if (useDetailData) {
                  // Count from detail items
                  const courseCategories = ['cours_hebdo_proprio', 'cours_collectif_ponctuel', 'cours_particulier', 'cours_inclus_forfait', 'cours_hors_forfait'];
                  coursPendingCount = 0;
                  coursInvoicedCount = 0;
                  courseCategories.forEach(cat => {
                    const items = cavalierDetail.items?.[cat] || [];
                    coursPendingCount += items.filter(i => i.status === 'pending').length;
                    coursInvoicedCount += items.filter(i => i.status === 'invoiced').length;
                  });
                } else {
                  coursPendingCount = (sum.cours_hebdo_proprio?.pending || 0) + (sum.cours_collectif_ponctuel?.pending || 0) + (sum.cours_particulier?.pending || 0);
                  coursInvoicedCount = (sum.cours_hebdo_proprio?.invoiced || 0) + (sum.cours_collectif_ponctuel?.invoiced || 0) + (sum.cours_particulier?.invoiced || 0);
                }
                const coursDisplay = (filter.showPending ? coursPendingCount : 0) + (filter.showInvoiced ? coursInvoicedCount : 0);
                
                // Calculate forfait counts based on filter
                let forfaitPending, forfaitInvoiced;
                if (useDetailData) {
                  const forfaitItems = cavalierDetail.items?.forfait || [];
                  forfaitPending = forfaitItems.filter(i => i.status === 'pending').length;
                  forfaitInvoiced = forfaitItems.filter(i => i.status === 'invoiced').length;
                } else {
                  forfaitPending = (sum.forfait?.pending || 0);
                  forfaitInvoiced = (sum.forfait?.invoiced || 0);
                }
                const forfaitDisplay = (filter.showPending ? forfaitPending : 0) + (filter.showInvoiced ? forfaitInvoiced : 0);
                
                // Calculate pension/DP counts based on filter
                let pensionPending, pensionInvoiced;
                if (useDetailData) {
                  const pensionItems = cavalierDetail.items?.pension || [];
                  const dpItems = cavalierDetail.items?.demi_pension || [];
                  pensionPending = pensionItems.filter(i => i.status === 'pending').length + dpItems.filter(i => i.status === 'pending').length;
                  pensionInvoiced = pensionItems.filter(i => i.status === 'invoiced').length + dpItems.filter(i => i.status === 'invoiced').length;
                } else {
                  pensionPending = (sum.pension?.pending || 0) + (sum.demi_pension?.pending || 0);
                  pensionInvoiced = (sum.pension?.invoiced || 0) + (sum.demi_pension?.invoiced || 0);
                }
                const pensionDisplay = (filter.showPending ? pensionPending : 0) + (filter.showInvoiced ? pensionInvoiced : 0);
                
                return (
                  <React.Fragment key={cav.id}>
                    <TableRow 
                      data-cavalier-id={cav.id}
                      className={`cursor-pointer hover:bg-stone-50 ${isExpanded ? 'bg-stone-50' : ''}`}
                      onClick={() => handleExpandCavalier(cav.id)}
                    >
                      <TableCell>
                        {isExpanded ? (
                          <ChevronDown className="w-4 h-4 text-stone-400" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-stone-400" />
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">
                          {cav.prenom} {cav.nom}
                          {cav.pseudo && <span className="text-stone-500 font-normal ml-1">({cav.pseudo})</span>}
                        </div>
                        <div className="text-xs text-stone-400">{cav.email}</div>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge variant="outline" className="text-xs">
                          {roleLabels[cav.role_club] || cav.role_club}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-center gap-3">
                          <label className="flex items-center gap-1 cursor-pointer">
                            <Checkbox
                              checked={filter.showPending}
                              onCheckedChange={() => toggleCavalierFilter(cav.id, 'showPending')}
                              className="data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600"
                            />
                            <span className="text-xs text-amber-600">À fact.</span>
                          </label>
                          <label className="flex items-center gap-1 cursor-pointer">
                            <Checkbox
                              checked={filter.showInvoiced}
                              onCheckedChange={() => toggleCavalierFilter(cav.id, 'showInvoiced')}
                              className="data-[state=checked]:bg-green-600 data-[state=checked]:border-green-600"
                            />
                            <span className="text-xs text-green-600">Facturés</span>
                          </label>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {coursDisplay > 0 ? (
                          <span className="text-sm">{coursDisplay}</span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        {forfaitDisplay > 0 ? (
                          <span className="text-sm">{forfaitDisplay}</span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        {pensionDisplay > 0 ? (
                          <span className="text-sm">{pensionDisplay}</span>
                        ) : '-'}
                      </TableCell>
                      <TableCell className="text-right font-medium text-amber-700">
                        {displayPending > 0 ? formatAmount(displayPending) : '-'}
                      </TableCell>
                      <TableCell className="text-right font-medium text-green-700">
                        {displayInvoiced > 0 ? formatAmount(displayInvoiced) : '-'}
                      </TableCell>
                    </TableRow>
                    
                    {/* Expanded detail row */}
                    {isExpanded && (
                      <TableRow>
                        <TableCell colSpan={9} className="bg-stone-50/50 p-0">
                          {loadingDetail ? (
                            <div className="flex items-center justify-center py-8">
                              <Loader2 className="w-6 h-6 animate-spin text-[#2E4A3D]" />
                            </div>
                          ) : cavalierDetail ? (
                            <div className="p-4 space-y-4">
                              {/* Actions bar for this cavalier - only show when items selected */}
                              {isAdmin() && selectedItems.length > 0 && (
                                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                                  <div className="text-sm text-blue-800">
                                    <strong>{selectedItems.length}</strong> élément(s) sélectionné(s)
                                  </div>
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="border-blue-300 text-blue-700 hover:bg-blue-100"
                                        disabled={actionLoading}
                                      >
                                        <Menu className="w-4 h-4 mr-2" />
                                        Actions
                                        <ChevronDown className="w-4 h-4 ml-2" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-56">
                                      {/* Option 1: Créer facture Equibook directement - seulement si module facturation activé */}
                                      {facturationEnabled ? (
                                        <DropdownMenuItem
                                          onClick={() => openCreateInvoiceFromSelection()}
                                          className="text-blue-700 focus:text-blue-800 focus:bg-blue-50"
                                        >
                                          <Receipt className="w-4 h-4 mr-2" />
                                          Facturer la sélection
                                          <span className="ml-auto text-xs text-blue-500">Equibook</span>
                                        </DropdownMenuItem>
                                      ) : (
                                        <DropdownMenuItem
                                          disabled
                                          className="text-stone-400 cursor-not-allowed"
                                          title="Activez le module Facturation dans les paramètres du club"
                                        >
                                          <Receipt className="w-4 h-4 mr-2" />
                                          Facturer la sélection
                                          <span className="ml-auto text-xs text-stone-400">désactivé</span>
                                        </DropdownMenuItem>
                                      )}
                                      {/* Option 2: Simple marquage pour facturation externe */}
                                      <DropdownMenuItem
                                        onClick={() => handleMarkInvoiced(selectedItems)}
                                        className="text-green-700 focus:text-green-800 focus:bg-green-50"
                                      >
                                        <CheckCircle className="w-4 h-4 mr-2" />
                                        Marquer comme facturé
                                        <span className="ml-auto text-xs text-stone-400">externe</span>
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem
                                        onClick={() => handleBulkExclude(selectedItems)}
                                        className="text-red-700 focus:text-red-800 focus:bg-red-50"
                                      >
                                        <Ban className="w-4 h-4 mr-2" />
                                        Exclure la sélection
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        onClick={openBulkChangeTypeDialog}
                                        className="text-stone-700 focus:text-stone-800 focus:bg-stone-50"
                                      >
                                        <ArrowRightLeft className="w-4 h-4 mr-2" />
                                        Modifier la sélection
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                </div>
                              )}
                              
                              {/* Show summary based on filtered items - only when no items selected */}
                              {selectedItems.length === 0 && (() => {
                                const cavFilter = getCavalierFilter(expandedCavalier);
                                const displayPending = cavFilter.showPending ? cavalierDetail.totals?.pending : 0;
                                const displayInvoiced = cavFilter.showInvoiced ? cavalierDetail.totals?.invoiced : 0;
                                
                                return (
                                  <div className="flex items-center justify-between p-3 bg-stone-50 rounded-lg border border-stone-200">
                                    <div className="text-sm flex items-center gap-4">
                                      {displayPending > 0 && (
                                        <span className="text-amber-800">
                                          <strong>{formatAmount(displayPending)}</strong> à facturer
                                        </span>
                                      )}
                                      {displayInvoiced > 0 && (
                                        <span className="text-green-800">
                                          <strong>{formatAmount(displayInvoiced)}</strong> facturé
                                        </span>
                                      )}
                                      {displayPending === 0 && displayInvoiced === 0 && (
                                        <span className="text-stone-500">Aucun élément</span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      {isAdmin() && (
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          className="border-green-300 text-green-700 hover:bg-green-50"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setShowCreateItemDialog(true);
                                          }}
                                        >
                                          <PlusCircle className="w-4 h-4 mr-2" />
                                          Ajouter une ligne
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })()}
                              
                              {/* Detail by category */}
                              {Object.entries(cavalierDetail.items || {}).map(([category, items]) => {
                                if (!items || items.length === 0) return null;
                                const config = categoryConfig[category] || { label: category, color: 'bg-gray-100 text-gray-800' };
                                
                                // Pour cours_inclus_forfait, pas de notion "à facturer"
                                const isIncludedCategory = category === 'cours_inclus_forfait';
                                
                                // Get the filter for this cavalier
                                const cavFilter = getCavalierFilter(expandedCavalier);
                                
                                // Filtrer les items selon les checkboxes du cavalier
                                const filteredItems = items.filter(item => {
                                  if (item.status === 'pending' && cavFilter.showPending) return true;
                                  if (item.status === 'invoiced' && cavFilter.showInvoiced) return true;
                                  return false;
                                });
                                
                                // Si après filtrage il n'y a plus d'items, ne pas afficher la catégorie
                                if (filteredItems.length === 0) return null;
                                
                                const pendingItems = isIncludedCategory ? [] : filteredItems.filter(item => item.status === 'pending');
                                const invoiceableItems = pendingItems.filter(item => !isItemInFuture(item));
                                const allInvoiceableSelected = invoiceableItems.length > 0 && invoiceableItems.every(item => selectedItems.includes(item.id));
                                
                                return (
                                  <div key={category} className="border rounded-lg overflow-hidden">
                                    <div className={`px-3 py-2 ${config.color} font-medium text-sm flex items-center gap-2`}>
                                      {isAdmin() && invoiceableItems.length > 0 && (
                                        <Checkbox
                                          checked={allInvoiceableSelected}
                                          onCheckedChange={(checked) => {
                                            const invoiceableIds = invoiceableItems.map(item => item.id);
                                            if (checked) {
                                              setSelectedItems([...new Set([...selectedItems, ...invoiceableIds])]);
                                            } else {
                                              setSelectedItems(selectedItems.filter(id => !invoiceableIds.includes(id)));
                                            }
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                          className="mr-1"
                                        />
                                      )}
                                      <CategoryIcon type={category} className="w-4 h-4" />
                                      {config.label}
                                      <Badge variant="secondary" className="ml-auto">
                                        {isIncludedCategory ? (
                                          <span className="text-green-600">{filteredItems.length} inclus</span>
                                        ) : (
                                          <>
                                            {filteredItems.filter(i => i.status === 'pending').length > 0 && (
                                              <span className="text-amber-600 mr-1">{filteredItems.filter(i => i.status === 'pending').length} à fact.</span>
                                            )}
                                            {filteredItems.filter(i => i.status === 'invoiced').length > 0 && (
                                              <span className="text-green-600">{filteredItems.filter(i => i.status === 'invoiced').length} fact.</span>
                                            )}
                                          </>
                                        )}
                                      </Badge>
                                    </div>
                                    <Table>
                                      <TableBody>
                                        {filteredItems.map((item) => (
                                          <TableRow key={item.id} data-item-id={item.id} className="text-sm group transition-colors duration-300">
                                            <TableCell className="w-8">
                                              {isAdmin() && item.status === 'pending' && item.item_type !== 'cours_inclus_forfait' && (
                                                <Checkbox
                                                  checked={selectedItems.includes(item.id)}
                                                  onCheckedChange={(checked) => {
                                                    if (checked) {
                                                      setSelectedItems([...selectedItems, item.id]);
                                                    } else {
                                                      setSelectedItems(selectedItems.filter(id => id !== item.id));
                                                    }
                                                  }}
                                                  onClick={(e) => e.stopPropagation()}
                                                  disabled={isItemInFuture(item)}
                                                />
                                              )}
                                            </TableCell>
                                            <TableCell className="flex-1">
                                              {item.label}
                                              {item.details?.date && (
                                                <span className={`text-xs ml-2 ${isItemInFuture(item) ? 'text-orange-500 font-medium' : 'text-stone-400'}`}>
                                                  {safeFormatDate(item.details.date, 'd MMM yyyy')}
                                                  {isItemInFuture(item) && ' (futur)'}
                                                </span>
                                              )}
                                            </TableCell>
                                            <TableCell className="text-right font-medium">
                                              {/* Cours inclus forfait: juste afficher "Inclus" */}
                                              {item.item_type === 'cours_inclus_forfait' ? (
                                                <span className="text-green-600 text-sm">Inclus</span>
                                              ) : item.amount_to_define || (item.amount === 0 && item.item_type !== 'cours_inclus_forfait') ? (
                                                <span className="text-amber-600 italic flex items-center justify-end gap-1">
                                                  À définir
                                                  {isAdmin() && !isItemLocked(item) && (
                                                    <Button
                                                      variant="ghost"
                                                      size="sm"
                                                      className="h-6 px-1.5 text-amber-600"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setEditAmountItem(item);
                                                        setEditAmountValue('');
                                                      }}
                                                    >
                                                      <Pencil className="w-3 h-3" />
                                                    </Button>
                                                  )}
                                                </span>
                                              ) : (
                                                <span className="flex items-center justify-end gap-1">
                                                  {formatAmount(item.amount)}
                                                  {isAdmin() && !isItemLocked(item) && (
                                                    <Button
                                                      variant="ghost"
                                                      size="sm"
                                                      className="h-6 px-1.5 text-stone-300 hover:text-stone-600"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        setEditAmountItem(item);
                                                        setEditAmountValue(item.amount?.toString() || '');
                                                      }}
                                                      title="Modifier le montant"
                                                    >
                                                      <Pencil className="w-3 h-3" />
                                                    </Button>
                                                  )}
                                                </span>
                                              )}
                                            </TableCell>
                                            {/* Colonne "Facturé le" pour les items facturés */}
                                            <TableCell className="w-24 text-xs text-stone-500">
                                              {item.status === 'invoiced' && item.invoiced_at && (
                                                <div className="flex items-center gap-1">
                                                  {isItemLocked(item) && (
                                                    <Lock className="w-3 h-3 text-blue-500" title={item.invoice_numero ? `Lié à la facture ${item.invoice_numero}` : 'Brouillon en attente'} />
                                                  )}
                                                  <span>{safeFormatDate(item.invoiced_at, 'd MMM')}</span>
                                                </div>
                                              )}
                                            </TableCell>
                                            <TableCell className="w-28">
                                              {/* Pas de badge pour les cours inclus forfait */}
                                              {item.item_type === 'cours_inclus_forfait' ? null : (
                                                <>
                                                  {item.status === 'pending' && !isItemInFuture(item) && (
                                                    <Badge className="bg-amber-100 text-amber-800">
                                                      <Clock className="w-3 h-3 mr-1" />
                                                      À facturer
                                                    </Badge>
                                                  )}
                                                  {item.status === 'invoiced' && (
                                                    <Badge className={isItemLocked(item) ? (item.invoice_numero ? "bg-blue-100 text-blue-800" : "bg-amber-100 text-amber-800") : "bg-green-100 text-green-800"}>
                                                      {isItemLocked(item) ? (
                                                        <Lock className="w-3 h-3 mr-1" />
                                                      ) : (
                                                        <CheckCircle className="w-3 h-3 mr-1" />
                                                      )}
                                                      {isItemLocked(item) ? (item.invoice_numero ? 'Facturé' : 'Brouillon') : 'Facturé (ext.)'}
                                                    </Badge>
                                                  )}
                                                  {item.status === 'excluded' && (
                                                    <Badge className="bg-stone-100 text-stone-600">
                                                      <Ban className="w-3 h-3 mr-1" />
                                                      Exclu
                                                    </Badge>
                                                  )}
                                                </>
                                              )}
                                            </TableCell>
                                            <TableCell className="w-24">
                                              {isAdmin() && !isItemLocked(item) && (
                                                <div className="flex items-center gap-1">
                                                  {/* Pour cours inclus forfait: boutons convertir ET modifier type */}
                                                  {item.item_type === 'cours_inclus_forfait' ? (
                                                    <>
                                                      <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-7 px-2 text-orange-500 hover:text-orange-700 hover:bg-orange-50"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setConvertItem(item);
                                                          setShowConvertDialog(true);
                                                        }}
                                                        title="Convertir en cours hors forfait"
                                                      >
                                                        <ArrowRightLeft className="w-4 h-4" />
                                                      </Button>
                                                      <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-7 px-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setChangeTypeItem(item);
                                                          setSelectedNewType('cours_hors_forfait');
                                                          setShowChangeTypeDialog(true);
                                                        }}
                                                        title="Modifier le type de cours"
                                                      >
                                                        <Pencil className="w-4 h-4" />
                                                      </Button>
                                                    </>
                                                  ) : item.status === 'pending' && !isItemInFuture(item) && (
                                                    <>
                                                      <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-7 px-2 text-green-600 hover:text-green-700 hover:bg-green-50"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          handleMarkInvoiced(item.id);
                                                        }}
                                                        disabled={actionLoading}
                                                        title="Marquer comme facturé"
                                                      >
                                                        <Check className="w-4 h-4" />
                                                      </Button>
                                                      <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-7 px-2 text-blue-500 hover:text-blue-700 hover:bg-blue-50"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setChangeTypeItem(item);
                                                          setSelectedNewType(item.item_type);
                                                          setShowChangeTypeDialog(true);
                                                        }}
                                                        title="Modifier le type de cours"
                                                      >
                                                        <Pencil className="w-4 h-4" />
                                                      </Button>
                                                      <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        className="h-7 px-2 text-stone-500 hover:text-stone-700 hover:bg-stone-100"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setExcludeItem(item);
                                                          setShowExcludeDialog(true);
                                                        }}
                                                        title="Exclure de la facturation"
                                                      >
                                                        <X className="w-4 h-4" />
                                                      </Button>
                                                    </>
                                                  )}
                                                  {(item.status === 'invoiced' || item.status === 'excluded') && (
                                                    <Button
                                                      variant="ghost"
                                                      size="sm"
                                                      className="h-7 px-2 text-stone-400 hover:text-stone-600"
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleResetStatus(item.id);
                                                      }}
                                                      disabled={actionLoading}
                                                      title="Défacturer"
                                                    >
                                                      <RefreshCcw className="w-4 h-4" />
                                                    </Button>
                                                  )}
                                                </div>
                                              )}
                                              {isItemLocked(item) && (
                                                <span 
                                                  className={`text-xs ${item.invoice_numero ? 'text-blue-500' : 'text-amber-500'}`} 
                                                  title={item.invoice_numero ? `Facture ${item.invoice_numero}` : 'Brouillon en attente'}
                                                >
                                                  {item.invoice_numero || 'Brouillon'}
                                                </span>
                                              )}
                                            </TableCell>
                                          </TableRow>
                                        ))}
                                      </TableBody>
                                    </Table>
                                  </div>
                                );
                              })}
                              
                              {/* Summary for this cavalier */}
                              <div className="flex items-center justify-end gap-4 pt-2 border-t">
                                <div className="text-sm">
                                  <span className="text-stone-500">À facturer:</span>
                                  <span className="font-bold text-amber-700 ml-2">
                                    {formatAmount(cavalierDetail.totals?.pending)}
                                  </span>
                                </div>
                                <div className="text-sm">
                                  <span className="text-stone-500">Facturé:</span>
                                  <span className="font-bold text-green-700 ml-2">
                                    {formatAmount(cavalierDetail.totals?.invoiced)}
                                  </span>
                                </div>
                              </div>
                              
                              {/* Invoiced groups - Créer une facture (seulement si module facturation activé) */}
                              {isAdmin() && facturationEnabled && getCavalierFilter(expandedCavalier).showInvoiced && (() => {
                                const groups = getInvoicedGroups();
                                const unlockedGroups = groups.filter(g => !g.locked);
                                
                                if (unlockedGroups.length === 0) return null;
                                
                                return (
                                  <div className="mt-4 pt-4 border-t border-dashed">
                                    <div className="text-sm font-medium text-stone-700 mb-3 flex items-center gap-2">
                                      <Receipt className="w-4 h-4" />
                                      Créer une facture depuis les éléments facturés
                                    </div>
                                    <div className="space-y-2">
                                      {unlockedGroups.map((group) => (
                                        <div 
                                          key={group.date}
                                          className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200"
                                        >
                                          <div>
                                            <div className="font-medium text-green-800">
                                              Facturé le {safeFormatDate(group.date, 'd MMMM yyyy', { fallback: 'date inconnue' })}
                                            </div>
                                            <div className="text-sm text-green-600">
                                              {group.items.length} élément(s) - {formatAmount(group.total)}
                                            </div>
                                          </div>
                                          <Button
                                            size="sm"
                                            className="bg-green-600 hover:bg-green-700"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              openCreateInvoiceDialog(group);
                                            }}
                                          >
                                            <Receipt className="w-4 h-4 mr-2" />
                                            Créer la facture
                                          </Button>
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                );
              })}
              
              {filteredCavaliers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-stone-500">
                    {searchTerm || roleFilter !== 'all' 
                      ? 'Aucun cavalier ne correspond aux filtres'
                      : 'Aucune donnée pour ce trimestre'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Exclude dialog */}
      <Dialog open={showExcludeDialog} onOpenChange={setShowExcludeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Exclure de la facturation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {excludeItem && (
              <div className="p-3 bg-stone-50 rounded-lg">
                <div className="font-medium">{excludeItem.label}</div>
                <div className="text-sm text-stone-500">{formatAmount(excludeItem.amount)}</div>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-stone-700">Raison de l'exclusion *</label>
              <Textarea
                value={excludeReason}
                onChange={(e) => setExcludeReason(e.target.value)}
                placeholder="Ex: Offert, erreur de saisie, accord commercial..."
                className="mt-1"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowExcludeDialog(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleExclude}
                disabled={!excludeReason.trim() || actionLoading}
                className="bg-stone-600 hover:bg-stone-700"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Exclure
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit amount dialog */}
      <Dialog open={!!editAmountItem} onOpenChange={(open) => !open && setEditAmountItem(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Définir le montant</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {editAmountItem && (
              <div className="p-3 bg-stone-50 rounded-lg">
                <div className="font-medium">{editAmountItem.label}</div>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-stone-700">Montant (€) *</label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={editAmountValue}
                onChange={(e) => setEditAmountValue(e.target.value)}
                placeholder="Ex: 335"
                className="mt-1"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditAmountItem(null)}>
                Annuler
              </Button>
              <Button 
                onClick={handleUpdateAmount}
                disabled={!editAmountValue || actionLoading}
                className="bg-[#2E4A3D] hover:bg-[#243D32]"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Enregistrer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Convert to hors forfait dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-orange-500" />
              Convertir en cours hors forfait
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {convertItem && (
              <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                <div className="font-medium text-orange-800">{convertItem.label}</div>
                <div className="text-sm text-orange-600 mt-1">
                  Ce cours sera converti de "inclus dans le forfait" à "hors forfait" et deviendra facturable.
                </div>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-stone-700">Montant à facturer (€) *</label>
              <Input
                type="number"
                min="0"
                step="0.01"
                defaultValue="25"
                id="convert-amount"
                placeholder="Ex: 25"
                className="mt-1"
              />
              <p className="text-xs text-stone-500 mt-1">Tarif standard cours hors forfait : 25€</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setShowConvertDialog(false);
                setConvertItem(null);
              }}>
                Annuler
              </Button>
              <Button 
                onClick={() => {
                  const amount = document.getElementById('convert-amount').value;
                  if (amount && convertItem) {
                    handleConvertToHorsForfait(convertItem, amount);
                  }
                }}
                disabled={actionLoading}
                className="bg-orange-600 hover:bg-orange-700"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Convertir et facturer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change course type dialog */}
      <Dialog open={showChangeTypeDialog} onOpenChange={setShowChangeTypeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-blue-500" />
              Modifier le type de cours
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {changeTypeItem && (
              <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="font-medium text-blue-800">{changeTypeItem.label}</div>
                <div className="text-sm text-blue-600 mt-1">
                  Montant actuel : {formatAmount(changeTypeItem.amount)}
                </div>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-stone-700 mb-2 block">Nouveau type *</label>
              <div className="space-y-2">
                {courseTypeOptions.map((option) => (
                  <label 
                    key={option.value}
                    className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedNewType === option.value 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-stone-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="courseType"
                        value={option.value}
                        checked={selectedNewType === option.value}
                        onChange={(e) => setSelectedNewType(e.target.value)}
                        className="text-blue-600"
                      />
                      <span className="font-medium">{option.label}</span>
                    </div>
                    <span className="text-stone-500">{option.defaultAmount}€</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700">Montant (€) *</label>
              <Input
                type="number"
                min="0"
                step="0.01"
                id="change-type-amount"
                value={courseTypeOptions.find(o => o.value === selectedNewType)?.defaultAmount || changeTypeItem?.amount || ''}
                onChange={(e) => {}}
                placeholder="Montant"
                className="mt-1"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setShowChangeTypeDialog(false);
                setChangeTypeItem(null);
                setSelectedNewType('');
              }}>
                Annuler
              </Button>
              <Button 
                onClick={() => {
                  const amount = document.getElementById('change-type-amount').value;
                  if (amount && changeTypeItem && selectedNewType) {
                    handleChangeType(changeTypeItem, selectedNewType, amount);
                  }
                }}
                disabled={actionLoading || !selectedNewType}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Appliquer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Change Type Dialog */}
      <Dialog open={showBulkChangeTypeDialog} onOpenChange={setShowBulkChangeTypeDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowRightLeft className="w-5 h-5 text-blue-500" />
              Modifier {bulkChangeTypeItems.length} élément(s)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 max-h-32 overflow-y-auto">
              {bulkChangeTypeItems.map((item, idx) => (
                <div key={item.id} className="text-sm text-blue-800">
                  {idx + 1}. {item.label} ({formatAmount(item.amount)})
                </div>
              ))}
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 mb-2 block">Nouveau type pour tous *</label>
              <div className="space-y-2">
                {courseTypeOptions.map((option) => (
                  <label 
                    key={option.value}
                    className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${
                      bulkSelectedNewType === option.value 
                        ? 'border-blue-500 bg-blue-50' 
                        : 'border-stone-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="bulkCourseType"
                        value={option.value}
                        checked={bulkSelectedNewType === option.value}
                        onChange={(e) => {
                          setBulkSelectedNewType(e.target.value);
                          setBulkNewAmount(option.defaultAmount.toString());
                        }}
                        className="text-blue-600"
                      />
                      <span className="font-medium">{option.label}</span>
                    </div>
                    <span className="text-stone-500">{option.defaultAmount}€</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700">Montant pour tous (€) *</label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={bulkNewAmount}
                onChange={(e) => setBulkNewAmount(e.target.value)}
                placeholder="Montant"
                className="mt-1"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setShowBulkChangeTypeDialog(false);
                setBulkChangeTypeItems([]);
                setBulkSelectedNewType('');
                setBulkNewAmount('');
              }}>
                Annuler
              </Button>
              <Button 
                onClick={handleBulkChangeType}
                disabled={actionLoading || !bulkSelectedNewType || !bulkNewAmount}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Appliquer à {bulkChangeTypeItems.length} élément(s)
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create new item dialog */}
      <Dialog open={showCreateItemDialog} onOpenChange={setShowCreateItemDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PlusCircle className="w-5 h-5 text-green-500" />
              Ajouter une ligne de facturation
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {cavalierDetail && (
              <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                <div className="font-medium text-green-800">
                  {cavalierDetail.cavalier?.prenom} {cavalierDetail.cavalier?.nom}
                </div>
                <div className="text-sm text-green-600">
                  Trimestre : {selectedTrimester?.label}
                </div>
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium text-stone-700 mb-2 block">Type *</label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {newItemTypeOptions.map((option) => (
                  <label 
                    key={option.value}
                    className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${
                      newItemType === option.value 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-stone-200 hover:border-green-300'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="newItemType"
                        value={option.value}
                        checked={newItemType === option.value}
                        onChange={(e) => {
                          setNewItemType(e.target.value);
                          setNewItemAmount(option.defaultAmount.toString());
                          if (option.value !== 'autre') {
                            setNewItemLabel(option.label);
                          } else {
                            setNewItemLabel('');
                          }
                        }}
                        className="text-green-600"
                      />
                      <span className="font-medium">{option.label}</span>
                    </div>
                    {option.defaultAmount > 0 && (
                      <span className="text-stone-500">{option.defaultAmount}€</span>
                    )}
                  </label>
                ))}
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium text-stone-700">Libellé *</label>
              <Input
                type="text"
                value={newItemLabel}
                onChange={(e) => setNewItemLabel(e.target.value)}
                placeholder="Ex: Cours particulier - 15/02/2026"
                className="mt-1"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-stone-700">Montant (€) *</label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={newItemAmount}
                onChange={(e) => setNewItemAmount(e.target.value)}
                placeholder="Montant"
                className="mt-1"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-stone-700">Date (optionnel)</label>
              <Input
                type="date"
                value={newItemDate}
                onChange={(e) => setNewItemDate(e.target.value)}
                className="mt-1"
              />
            </div>
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setShowCreateItemDialog(false);
                setNewItemType('');
                setNewItemLabel('');
                setNewItemAmount('');
                setNewItemDate('');
              }}>
                Annuler
              </Button>
              <Button 
                onClick={handleCreateItem}
                disabled={actionLoading || !newItemType || !newItemLabel || !newItemAmount}
                className="bg-green-600 hover:bg-green-700"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Ajouter
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create invoice from decompte dialog */}
      <Dialog open={showCreateInvoiceDialog} onOpenChange={setShowCreateInvoiceDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="w-5 h-5 text-blue-500" />
              Créer une facture
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center justify-between">
                <div className="font-medium text-blue-800">
                  Facturé le {safeFormatDate(invoiceGroupDate, 'd MMMM yyyy')}
                </div>
                <div className="text-blue-800 font-bold">
                  {formatAmount(invoiceGroupItems.reduce((sum, i) => sum + (i.amount || 0), 0))}
                </div>
              </div>
            </div>
            
            <div className="max-h-64 overflow-y-auto border rounded-lg">
              <Table>
                <TableBody>
                  {invoiceGroupItems.map((item, idx) => (
                    <TableRow key={item.id} className="text-sm">
                      <TableCell className="py-2">{item.label}</TableCell>
                      <TableCell className="py-2 text-right font-medium">{formatAmount(item.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">Une fois la facture créée :</p>
                  <ul className="list-disc ml-4 mt-1 space-y-1">
                    <li>Les éléments seront <strong>verrouillés</strong> et ne pourront plus être modifiés</li>
                    <li>La facture apparaîtra dans l'onglet <strong>Facturation</strong></li>
                    <li>Pour débloquer les éléments, vous devrez créer un <strong>avoir</strong></li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => {
                setShowCreateInvoiceDialog(false);
                setInvoiceGroupItems([]);
                setInvoiceGroupDate('');
              }}>
                Annuler
              </Button>
              <Button 
                onClick={handleCreateInvoiceFromDecompte}
                disabled={creatingInvoice || invoiceGroupItems.length === 0}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {creatingInvoice ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Receipt className="w-4 h-4 mr-2" />}
                Créer la facture
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DecompteTab;
